import React, { useState } from "react";
import { useSignIn } from "@clerk/clerk-react";
import { markClerkOAuthPending } from "../lib/clerkBridge";

type OAuthStrategy = "oauth_google" | "oauth_facebook" | "oauth_apple";

type Props = {
  mode: "signin" | "signup";
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.9 26.8 37 24 37c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-1.1 3.1-3.5 5.5-6.1 6.9l.1.1 6.2 5.2C34.8 41.3 44 34 44 24c0-1.3-.1-2.7-.4-3.5z"
    />
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M24 12.07C24 5.41 18.63 0 12 0S0 5.41 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.04V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.26h3.32l-.53 3.49h-2.79V24C19.62 23.1 24 18.1 24 12.07z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M16.37 12.63c.03-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.71-3.18-1.73-1.35-.14-2.64.8-3.32.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.83.86-3.58 2.18-1.54 2.66-.39 6.59 1.09 8.74.73 1.05 1.59 2.23 2.72 2.19 1.1-.05 1.51-.7 2.84-.7 1.32 0 1.7.7 2.85.68 1.18-.02 1.92-1.07 2.64-2.13.83-1.21 1.17-2.39 1.19-2.45-.03-.01-2.27-.87-2.26-3.51zM14.5 5.6c.6-.73 1-1.74.89-2.75-.86.03-1.91.57-2.53 1.3-.55.64-1.04 1.67-.91 2.65 1 .08 2.02-.5 2.55-1.2z" />
  </svg>
);

// Keep in sync with Clerk Dashboard → SSO connections.
const PROVIDERS: {
  strategy: OAuthStrategy;
  label: string;
  variant: "google" | "facebook" | "apple";
  Icon: React.FC;
}[] = [
  { strategy: "oauth_google", label: "Continue with Google", variant: "google", Icon: GoogleIcon },
  {
    strategy: "oauth_facebook",
    label: "Continue with Facebook",
    variant: "facebook",
    Icon: FacebookIcon,
  },
  { strategy: "oauth_apple", label: "Continue with Apple", variant: "apple", Icon: AppleIcon },
];

/**
 * Stacked social OAuth buttons. Always starts via sign-in so Clerk can
 * transfer new social accounts into sign-up automatically.
 */
export default function ClerkSocialButtons({ mode: _mode }: Props) {
  const { signIn, isLoaded } = useSignIn();
  const [busyStrategy, setBusyStrategy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startOAuth = async (strategy: OAuthStrategy) => {
    if (!isLoaded || !signIn) return;
    setError(null);
    setBusyStrategy(strategy);
    try {
      const redirectUrl = `${window.location.origin}/sso-callback`;
      const redirectUrlComplete = `${window.location.origin}/`;
      markClerkOAuthPending();
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl,
        redirectUrlComplete,
      });
    } catch (err) {
      console.error("Clerk OAuth error:", err);
      const message =
        err && typeof err === "object" && "errors" in err
          ? String(
              (err as { errors?: Array<{ longMessage?: string; message?: string }> })
                .errors?.[0]?.longMessage ||
                (err as { errors?: Array<{ message?: string }> }).errors?.[0]?.message ||
                "Social sign-in failed"
            )
          : err instanceof Error
            ? err.message
            : "Social sign-in failed";
      setError(message);
      setBusyStrategy(null);
    }
  };

  return (
    <div className="Auth-social" data-testid="auth-social-buttons">
      <div className="Auth-social-stack" role="group" aria-label="Continue with a social account">
        {PROVIDERS.map(({ strategy, label, variant, Icon }) => (
          <button
            key={strategy}
            type="button"
            className={`Auth-social-provider Auth-social-provider--${variant}`}
            data-testid={`auth-social-${strategy}`}
            disabled={!isLoaded || busyStrategy !== null}
            onClick={() => startOAuth(strategy)}
          >
            <span className="Auth-social-provider-icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="Auth-social-provider-label">
              {busyStrategy === strategy ? "Redirecting…" : label}
            </span>
          </button>
        ))}
      </div>
      {error ? (
        <p className="Auth-social-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
